import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ViewStyle,
} from 'react-native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

const ChevronDown = RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN;

export interface SettingsNavOption<T extends string> {
  key: T;
  title: string;
  description: string;
  /** Optional custom active color for this option */
  activeColor?: string;
}

interface SettingsNavHeaderProps<T extends string> {
  options: SettingsNavOption<T>[];
  selectedKey: T;
  onSelect: (key: T) => void;
  /** Default color for the active header background (can be overridden per option) */
  activeColor?: string;
  /** Color for the card background */
  cardBackgroundColor?: string;
  /** Primary text color */
  textPrimaryColor?: string;
  /** Muted text color */
  textMutedColor?: string;
  /** Custom container style */
  containerStyle?: ViewStyle;
}

/**
 * Reusable Settings Nav Header Component
 * Expandable accordion for switching between sections/tabs
 * Used in Linked Accounts, Account Settings, etc.
 */
export function SettingsNavHeader<T extends string>({
  options,
  selectedKey,
  onSelect,
  activeColor = '#4672FE',
  cardBackgroundColor = '#242424',
  textPrimaryColor = '#EEF1F4',
  textMutedColor = '#757575',
  containerStyle,
}: SettingsNavHeaderProps<T>) {
  const [isExpanded, setIsExpanded] = useState(false);

  const selectedOption = options.find(o => o.key === selectedKey) || options[0];
  
  // Get the active color for the selected option (use option-specific color if provided)
  const currentActiveColor = selectedOption.activeColor || activeColor;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleSelect = (key: T) => {
    onSelect(key);
    setIsExpanded(false);
  };

  return (
    <View style={[styles.container, { backgroundColor: cardBackgroundColor }, containerStyle]}>
      {/* Selected/Active Header */}
      <TouchableOpacity
        style={[styles.activeHeader, { backgroundColor: currentActiveColor }]}
        onPress={toggleExpanded}
        activeOpacity={0.8}
      >
        <View style={styles.headerContent}>
          <View style={styles.textContainer}>
            <Text style={[styles.activeTitle, { color: textPrimaryColor }]}>
              {selectedOption.title}
            </Text>
            <Text style={[styles.activeDescription, { color: textPrimaryColor }]}>
              {selectedOption.description}
            </Text>
          </View>
          <View style={[
            styles.chevronContainer,
            isExpanded && styles.chevronRotated,
          ]}>
            <ChevronDown width={20} height={20} color={textPrimaryColor} />
          </View>
        </View>
      </TouchableOpacity>

      {/* Expanded Options */}
      {isExpanded && (
        <View style={styles.expandedContainer}>
          {options.map((option) => {
            const isSelected = option.key === selectedKey;
            // Use option-specific color or fall back to current active color
            const indicatorColor = option.activeColor || currentActiveColor;
            
            return (
              <TouchableOpacity
                key={option.key}
                style={[
                  styles.optionItem,
                  isSelected && styles.optionItemSelected,
                ]}
                onPress={() => handleSelect(option.key)}
                activeOpacity={0.7}
              >
                {isSelected && (
                  <View style={[styles.leftIndicator, { backgroundColor: indicatorColor }]} />
                )}
                <View style={[
                  styles.optionContent,
                  isSelected && styles.optionContentSelected,
                ]}>
                  <Text style={[
                    styles.optionTitle,
                    { color: textPrimaryColor },
                    isSelected && { color: textMutedColor },
                  ]}>
                    {option.title}
                  </Text>
                  <Text style={[
                    styles.optionDescription,
                    { color: textMutedColor },
                  ]}>
                    {option.description}
                  </Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    overflow: 'hidden',
    marginHorizontal: 20,
    marginTop: 20,
  },
  activeHeader: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 6,
  },
  textContainer: {
    flex: 1,
    gap: 4,
  },
  activeTitle: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 21,
  },
  activeDescription: {
    fontSize: 12,
    fontWeight: '300',
    lineHeight: 18,
  },
  chevronContainer: {
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chevronRotated: {
    transform: [{ rotate: '180deg' }],
  },
  expandedContainer: {
    paddingTop: 0,
  },
  optionItem: {
    flexDirection: 'row',
    alignItems: 'stretch',
    paddingVertical: 16,
    paddingRight: 20,
    paddingLeft: 20,
    borderRadius: 16,
    overflow: 'hidden',
  },
  optionItemSelected: {
    paddingLeft: 0,
  },
  leftIndicator: {
    width: 3,
    marginRight: 17,
  },
  optionContent: {
    flex: 1,
    gap: 4,
  },
  optionContentSelected: {
    // No additional styling needed - indicator handles spacing
  },
  optionTitle: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 21,
  },
  optionDescription: {
    fontSize: 12,
    fontWeight: '300',
    lineHeight: 18,
  },
});

export default SettingsNavHeader;
